import {
  useCallback,
  useEffect,
  useRef,
  type HTMLAttributes,
} from "react";
import { useLocale } from "@seller/app/locale";

type Props = HTMLAttributes<HTMLDivElement> & {
  /** Width of the fade at each edge, in px. */
  fadeSize?: number;
};

/**
 * Horizontal scroll container that fades its content at whichever edge(s) have
 * hidden content — a scrollability cue. The fade appears at the inline-end
 * while more content lies ahead, at the inline-start once scrolled, on both
 * sides mid-scroll, and on neither when everything fits. RTL-aware. Pass the
 * usual scroller classes (overflow-x-auto, flex, gap, bleed) via `className`.
 *
 * The mask is written to the DOM imperatively on scroll/resize/content-change
 * rather than through React state — it's a scroll-driven visual, so this avoids
 * re-rendering the chip/KPI list on every scroll frame.
 */
export function HScroller({ className, children, fadeSize = 24, ...rest }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const isRtl = useLocale() === "ar";

  const apply = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    // abs() normalizes the RTL negative-scrollLeft model to a distance.
    const dist = Math.abs(el.scrollLeft);
    const start = max > 1 && dist > 1;
    const end = max > 1 && dist < max - 1;
    // `to right` is physical, so map the logical start/end fades onto it by
    // direction.
    const fadeLeft = isRtl ? end : start;
    const fadeRight = isRtl ? start : end;
    const stops = [
      fadeLeft ? "transparent 0" : "black 0",
      fadeLeft ? `black ${fadeSize}px` : null,
      fadeRight ? `black calc(100% - ${fadeSize}px)` : null,
      fadeRight ? "transparent 100%" : "black 100%",
    ]
      .filter(Boolean)
      .join(", ");
    const mask = `linear-gradient(to right, ${stops})`;
    el.style.setProperty("mask-image", mask);
    el.style.setProperty("-webkit-mask-image", mask);
  }, [isRtl, fadeSize]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.addEventListener("scroll", apply, { passive: true });
    window.addEventListener("resize", apply);
    // Catches the element resizing and (via the same callback) the initial
    // measurement once it's observed.
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", apply);
      window.removeEventListener("resize", apply);
      ro.disconnect();
    };
  }, [apply]);

  // Re-measure after content changes (e.g. chips/counts loading in). Mutates
  // the DOM only — no React state, so it doesn't cascade renders.
  useEffect(() => {
    apply();
  });

  return (
    <div ref={ref} className={className} {...rest}>
      {children}
    </div>
  );
}
